// src/private/src/pages/public/ProspectSchedulePage.jsx
//
// Public discovery-call scheduler for sales prospects. Reached from the
// prospect-accepted email at /book/<token> — no login; the unguessable
// prospect token in the URL is the credential (same pattern as
// ContractSignPage). Books against the PROSPECTING schedule via
// /api/meetings/prospects/* and holds at most ONE active booking per
// link (cancel to pick a new time).
//
// In request mode (the default, context.mode === 'request') the prospect
// doesn't book: they pick up to max_requested_times slots and staff confirm
// one, which then shows here as the booked call.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import VideoCallIcon from '@mui/icons-material/VideoCall';

import {
  cancelProspectBooking,
  createProspectBooking,
  fetchProspectAvailability,
  fetchProspectBookingContext,
  requestProspectTimes,
} from '@src/api/clientSchedule';
import { dayjs } from '@src/corelib';

const BRAND_ORANGE = '#ff680d';
const HEADER_BG = '#111827';
const LOGO_URL =
  'https://leadguruspro.s3.amazonaws.com/static/img/lead-gurus-logo-full-dark.png';

const TZ_SHORT_LABELS = {
  'America/New_York': 'ET',
  'America/Chicago': 'CT',
  'America/Denver': 'MT',
  'America/Phoenix': 'MST',
  'America/Los_Angeles': 'PT',
};

function resolveBrowserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch (e) {
    return '';
  }
}

// "Tuesdays & Wednesdays 1 – 5:30 PM, Thursdays 1 – 4 PM ET" from the
// live rule rows so the header can't drift from the slot engine.
function formatOfficeHours(rules) {
  if (!Array.isArray(rules) || rules.length === 0) return '';
  const groups = new Map();
  for (const rule of rules) {
    const key = `${rule.start_time}|${rule.end_time}|${rule.timezone}`;
    if (!groups.has(key)) groups.set(key, { ...rule, days: [] });
    groups.get(key).days.push(`${rule.weekday_display}s`);
  }
  const fmt = (hhmm) => {
    const [h, m] = hhmm.split(':').map(Number);
    const suffix = h >= 12 ? 'PM' : 'AM';
    const hour12 = ((h + 11) % 12) + 1;
    return m ? `${hour12}:${String(m).padStart(2, '0')} ${suffix}` : `${hour12} ${suffix}`;
  };
  return [...groups.values()]
    .map((g) => {
      const tz = TZ_SHORT_LABELS[g.timezone] || g.timezone;
      const days =
        g.days.length > 1
          ? `${g.days.slice(0, -1).join(', ')} & ${g.days[g.days.length - 1]}`
          : g.days[0];
      return `${days} ${fmt(g.start_time)} – ${fmt(g.end_time)} ${tz}`;
    })
    .join(', ');
}

export default function ProspectSchedulePage() {
  const { prospectToken } = useParams();

  const [context, setContext] = useState(null);
  const [loadState, setLoadState] = useState('loading'); // loading | ready | notfound | error
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [confirmSlot, setConfirmSlot] = useState(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [actionError, setActionError] = useState('');
  const [justBooked, setJustBooked] = useState(false);
  const [picked, setPicked] = useState([]);
  const [changingRequest, setChangingRequest] = useState(false);

  const booking = context?.booking || null;
  const isRequest = context?.mode === 'request';
  const pendingRequest = context?.pending_request || null;
  const maxPicks = context?.max_requested_times || 3;
  const canBook = Boolean(context?.can_book);
  const browserTz = resolveBrowserTimezone();

  const loadContext = useCallback(() => {
    fetchProspectBookingContext(prospectToken)
      // api.request resolves with the full axios response — unwrap .data.
      .then((res) => {
        setContext(res.data);
        setLoadState('ready');
      })
      .catch((err) => {
        setLoadState(err?.response?.status === 404 ? 'notfound' : 'error');
      });
  }, [prospectToken]);

  useEffect(() => {
    loadContext();
  }, [loadContext]);

  // Load slots whenever the picker should show (no active booking).
  useEffect(() => {
    if (loadState !== 'ready' || booking || !canBook) return;
    setSlotsLoading(true);
    fetchProspectAvailability(prospectToken)
      .then((res) => {
        const list = (res.data || []).map((s) => s.starts_at);
        setSlots(list);
        if (list.length > 0) {
          setSelectedDay(dayjs(list[0]).format('YYYY-MM-DD'));
        }
      })
      .catch(() => setActionError('Could not load available times. Please refresh.'))
      .finally(() => setSlotsLoading(false));
  }, [loadState, booking, canBook, prospectToken]);

  const slotsByDay = useMemo(() => {
    const map = {};
    for (const iso of slots) {
      const key = dayjs(iso).format('YYYY-MM-DD');
      (map[key] = map[key] || []).push(iso);
    }
    return map;
  }, [slots]);
  const availableDays = useMemo(() => Object.keys(slotsByDay), [slotsByDay]);

  const handleBook = () => {
    if (!confirmSlot) return;
    setSubmitting(true);
    setActionError('');
    createProspectBooking(prospectToken, {
      starts_at: confirmSlot,
      notes,
    })
      .then(() => {
        setConfirmSlot(null);
        setJustBooked(true);
        loadContext();
      })
      .catch((err) => {
        setActionError(
          err?.response?.data?.detail ||
            'That time is no longer available. Please pick another.',
        );
        setConfirmSlot(null);
        // Refresh both context (someone may have booked via another tab)
        // and slots so the stale time disappears.
        loadContext();
      })
      .finally(() => setSubmitting(false));
  };

  const togglePick = (iso) => {
    setPicked((prev) => {
      if (prev.includes(iso)) return prev.filter((x) => x !== iso);
      if (prev.length >= maxPicks) return prev;
      return [...prev, iso].sort();
    });
  };

  const handleRequest = () => {
    if (!picked.length) return;
    setSubmitting(true);
    setActionError('');
    requestProspectTimes(prospectToken, { times: picked, notes })
      .then(() => {
        setPicked([]);
        setChangingRequest(false);
        loadContext();
      })
      .catch((err) => {
        setActionError(
          err?.response?.data?.detail || 'One of those times was just taken. Please pick again.',
        );
        loadContext();
      })
      .finally(() => setSubmitting(false));
  };

  const handleCancel = () => {
    if (!booking) return;
    setSubmitting(true);
    setActionError('');
    cancelProspectBooking(prospectToken, booking.id)
      .then(() => {
        setCancelOpen(false);
        setJustBooked(false);
        loadContext();
      })
      .catch(() => setActionError('Could not cancel — please try again.'))
      .finally(() => setSubmitting(false));
  };

  const officeHoursCopy = formatOfficeHours(context?.office_hours);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f4f4f4', py: { xs: 2, sm: 4 } }}>
      <Box sx={{ maxWidth: 640, mx: 'auto', px: 2 }}>
        {/* Branded header, mirrors the prospect emails */}
        <Card sx={{ overflow: 'hidden', borderRadius: 2 }}>
          <Box sx={{ bgcolor: HEADER_BG, color: '#fff', p: 3, textAlign: 'center' }}>
            <img
              src={LOGO_URL}
              alt="Lead Gurus"
              style={{ display: 'block', maxHeight: 32, margin: '0 auto 12px' }}
            />
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {isRequest ? 'Request Your Strategy Call' : 'Schedule Your Discovery Call'}
            </Typography>
            {context?.company_name && (
              <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
                {context.company_name}
              </Typography>
            )}
          </Box>

          <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
            {loadState === 'loading' && (
              <Stack spacing={2}>
                <Skeleton variant="rounded" height={56} />
                <Skeleton variant="rounded" height={120} />
              </Stack>
            )}

            {loadState === 'notfound' && (
              <Alert severity="warning">
                This booking link isn&apos;t valid. Please use the link from
                your email, or reach out to{' '}
                <a href="mailto:getstarted@leadgurus.com">getstarted@leadgurus.com</a>.
              </Alert>
            )}

            {loadState === 'error' && (
              <Alert severity="error">
                Something went wrong loading this page. Please refresh, or email{' '}
                <a href="mailto:getstarted@leadgurus.com">getstarted@leadgurus.com</a>.
              </Alert>
            )}

            {loadState === 'ready' && (
              <>
                {actionError && (
                  <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError('')}>
                    {actionError}
                  </Alert>
                )}

                {booking ? (
                  <BookedCard
                    booking={booking}
                    justBooked={justBooked}
                    browserTz={browserTz}
                    onCancel={() => setCancelOpen(true)}
                  />
                ) : isRequest && pendingRequest && !changingRequest ? (
                  <PendingRequestCard
                    request={pendingRequest}
                    browserTz={browserTz}
                    onChange={() => setChangingRequest(true)}
                  />
                ) : !canBook ? (
                  <Alert severity="info">
                    This booking link is no longer active. If you still want to
                    talk, email{' '}
                    <a href="mailto:getstarted@leadgurus.com">getstarted@leadgurus.com</a>{' '}
                    and we&apos;ll get you a new one.
                  </Alert>
                ) : (
                  <>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                      <EventAvailableIcon sx={{ color: BRAND_ORANGE }} />
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {context.duration_minutes}-minute call
                          {context.contact_name ? ` with ${context.contact_name.split(' ')[0]} & the Lead Gurus team` : ''}
                        </Typography>
                        {officeHoursCopy && (
                          <Typography variant="caption" color="text.secondary">
                            Call hours: {officeHoursCopy}
                          </Typography>
                        )}
                      </Box>
                    </Stack>

                    {isRequest && (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Pick up to {maxPicks} times that work for you. We&apos;ll confirm one
                        within 24 hours and send the calendar invite.
                      </Alert>
                    )}

                    <SlotPicker
                      loading={slotsLoading}
                      slotsByDay={slotsByDay}
                      availableDays={availableDays}
                      selectedDay={selectedDay}
                      onSelectDay={setSelectedDay}
                      onSelectSlot={isRequest ? togglePick : setConfirmSlot}
                      picked={isRequest ? picked : []}
                      browserTz={browserTz}
                    />

                    {isRequest && (
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="overline" color="text.secondary">
                          Your picks ({picked.length}/{maxPicks})
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5, mb: 2 }}>
                          {picked.length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                              Tap times above to add them.
                            </Typography>
                          )}
                          {picked.map((iso) => (
                            <Chip
                              key={iso}
                              label={dayjs(iso).format('ddd, MMM D · h:mm A')}
                              onDelete={() => togglePick(iso)}
                              sx={{ bgcolor: BRAND_ORANGE, color: '#fff', '& .MuiChip-deleteIcon': { color: '#fff' } }}
                            />
                          ))}
                        </Stack>
                        <TextField
                          label="Anything you'd like us to know? (optional)"
                          multiline
                          minRows={2}
                          fullWidth
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          sx={{ mb: 2 }}
                        />
                        <Button
                          variant="contained"
                          fullWidth
                          disabled={!picked.length || submitting}
                          onClick={handleRequest}
                          sx={{ bgcolor: BRAND_ORANGE, '&:hover': { bgcolor: '#e05a08' }, py: 1.25, fontWeight: 600 }}
                          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                          {submitting ? 'Sending…' : 'Request these times'}
                        </Button>
                      </Box>
                    )}
                  </>
                )}
              </>
            )}
          </CardContent>

          <Divider />
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Lead Gurus ·{' '}
              <a href="https://leadgurus.com" style={{ color: 'inherit' }}>
                leadgurus.com
              </a>
            </Typography>
          </Box>
        </Card>
      </Box>

      {/* Confirm-booking dialog */}
      <Dialog open={Boolean(confirmSlot)} onClose={() => !submitting && setConfirmSlot(null)} fullWidth maxWidth="xs">
        <DialogTitle>Confirm your call</DialogTitle>
        <DialogContent>
          {confirmSlot && (
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
              {dayjs(confirmSlot).format('dddd, MMMM D · h:mm A')}
              {browserTz ? ` (${browserTz})` : ''}
            </Typography>
          )}
          <TextField
            label="Anything you'd like us to know? (optional)"
            multiline
            minRows={2}
            fullWidth
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSlot(null)} disabled={submitting}>
            Back
          </Button>
          <Button
            variant="contained"
            onClick={handleBook}
            disabled={submitting}
            sx={{ bgcolor: BRAND_ORANGE, '&:hover': { bgcolor: '#e05a08' } }}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {submitting ? 'Booking…' : 'Book it'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel dialog */}
      <Dialog open={cancelOpen} onClose={() => !submitting && setCancelOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Cancel this call?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            You can pick a new time right after cancelling.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)} disabled={submitting}>
            Keep it
          </Button>
          <Button color="error" variant="contained" onClick={handleCancel} disabled={submitting}>
            {submitting ? 'Cancelling…' : 'Cancel call'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function PendingRequestCard({ request, browserTz, onChange }) {
  return (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <CheckCircleOutlineIcon color="success" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Request received
          </Typography>
          <Typography variant="body2" color="text.secondary">
            We&apos;ll confirm one of these within 24 hours and email you the invite.
          </Typography>
        </Box>
      </Stack>
      <Stack spacing={1} sx={{ mb: 2 }}>
        {(request.times || []).map((iso) => (
          <Typography key={iso} variant="body1">
            {dayjs(iso).format('dddd, MMMM D · h:mm A')}
            {browserTz ? ` (${browserTz})` : ''}
          </Typography>
        ))}
      </Stack>
      <Divider sx={{ my: 2 }} />
      <Button variant="outlined" size="small" onClick={onChange} sx={{ color: BRAND_ORANGE, borderColor: BRAND_ORANGE }}>
        Pick different times
      </Button>
    </Box>
  );
}

function BookedCard({ booking, justBooked, browserTz, onCancel }) {
  return (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <CheckCircleOutlineIcon color="success" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {justBooked ? "You're booked!" : 'Your call is scheduled'}
          </Typography>
          <Typography variant="body1">
            {dayjs(booking.starts_at).format('dddd, MMMM D · h:mm A')}
            {browserTz ? ` (${browserTz})` : ''}
          </Typography>
        </Box>
      </Stack>

      <Alert severity="info" icon={<VideoCallIcon />} sx={{ mb: 2 }}>
        A calendar invite with the video-call link is on its way to your
        email. See you then!
      </Alert>

      <Stack direction="row" spacing={1}>
        <Chip label={`${booking.duration_minutes} minutes`} size="small" />
        {booking.zoom_join_url && <Chip label="Zoom" size="small" />}
      </Stack>

      <Divider sx={{ my: 2 }} />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Need a different time? Cancel below, then pick a new slot.
      </Typography>
      <Button variant="outlined" color="error" size="small" onClick={onCancel}>
        Cancel this call
      </Button>
    </Box>
  );
}

function SlotPicker({
  loading,
  slotsByDay,
  availableDays,
  selectedDay,
  onSelectDay,
  onSelectSlot,
  picked = [],
  browserTz,
}) {
  if (loading) {
    return (
      <Stack spacing={2}>
        <Stack direction="row" spacing={1}>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" width={92} height={64} sx={{ borderRadius: 2 }} />
          ))}
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} variant="rounded" width={84} height={32} sx={{ borderRadius: 999 }} />
          ))}
        </Stack>
      </Stack>
    );
  }

  if (availableDays.length === 0) {
    return (
      <Alert severity="info">
        No open times right now — new slots open up regularly, so please
        check back soon or email{' '}
        <a href="mailto:getstarted@leadgurus.com">getstarted@leadgurus.com</a>.
      </Alert>
    );
  }

  const slotsForSelected = selectedDay ? slotsByDay[selectedDay] || [] : [];

  return (
    <Box>
      <Typography variant="overline" color="text.secondary">
        Day
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 3, mt: 0.5 }}>
        {availableDays.map((day) => {
          const d = dayjs(day);
          const count = (slotsByDay[day] || []).length;
          const selected = selectedDay === day;
          return (
            <Button
              key={day}
              onClick={() => onSelectDay(day)}
              variant={selected ? 'contained' : 'outlined'}
              sx={{
                flexDirection: 'column',
                minWidth: 92,
                py: 0.75,
                px: 1.5,
                lineHeight: 1.3,
                textTransform: 'none',
                borderRadius: 2,
                ...(selected
                  ? { bgcolor: BRAND_ORANGE, '&:hover': { bgcolor: '#e05a08' } }
                  : { color: BRAND_ORANGE, borderColor: BRAND_ORANGE }),
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {d.format('ddd')} · {d.format('MMM D')}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.75 }}>
                {count} {count === 1 ? 'time' : 'times'}
              </Typography>
            </Button>
          );
        })}
      </Stack>

      <Stack direction="row" spacing={1} alignItems="baseline" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="overline" color="text.secondary">
          Time
        </Typography>
        {browserTz && (
          <Typography variant="caption" color="text.secondary">
            Times shown in your timezone: {browserTz}
          </Typography>
        )}
      </Stack>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {slotsForSelected.map((iso) => {
          const isPicked = picked.includes(iso);
          return (
            <Chip
              key={iso}
              label={dayjs(iso).format('h:mm A')}
              onClick={() => onSelectSlot(iso)}
              clickable
              sx={{
                borderRadius: 999,
                fontWeight: 600,
                ...(isPicked ? { bgcolor: BRAND_ORANGE, color: '#fff' } : {}),
                '&:hover': { bgcolor: BRAND_ORANGE, color: '#fff' },
              }}
            />
          );
        })}
      </Stack>
    </Box>
  );
}
